import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const mandelieuLaNapoule: ChauffeurPage = {
  slug: "mandelieu-la-napoule",
  title: "Chauffeur privé à Mandelieu-la-Napoule – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Mandelieu-la-Napoule : transferts aéroport de Nice, golf, port La Rague et Château de la Napoule, 7j/7.",
  keywords: [
    "chauffeur privé mandelieu",
    "vtc mandelieu-la-napoule",
    "chauffeur mandelieu",
    "transfert mandelieu aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Mandelieu-la-Napoule",
    subtitle:
      "Transferts aéroport de Nice, golfs, ports de plaisance et Château de la Napoule — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Mandelieu",
      "chauffeur La Napoule",
      "transfert aéroport Nice",
      "golf Cannes-Mandelieu",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Mandelieu-la-Napoule",
    badges: ["Disponible 24h/24", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Mandelieu-la-Napoule",
    items: [
      {
        title: "Aéroport de Nice en direct",
        description:
          "Aéroport de Nice à environ 40 min par l’A8, suivi de vol et accueil personnalisé au terminal.",
      },
      {
        title: "Golf & ports de plaisance",
        description:
          "Old Course de Cannes-Mandelieu, golf Riviera, ports La Rague et La Napoule : dépose au plus près, sans souci de stationnement.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, ponctuels et discrets, habitués à une clientèle golfique et internationale.",
      },
      {
        title: "Ancrage local",
        description: "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Mandelieu-la-Napoule",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge avec suivi de vol depuis et vers l’aéroport de Nice Côte d’Azur, l’aérodrome de Cannes-Mandelieu et les gares.",
      },
      {
        title: "Golf & séjours sportifs",
        text: "Liaisons vers l’Old Course de Cannes-Mandelieu et le golf Riviera, avec espace pour les sacs de golf.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, un chauffeur dédié pour la Route du Mimosa, l’Estérel et l’arrière-pays.",
      },
      {
        title: "Ports & plaisance",
        text: "Liaisons avec le port La Rague, le port de la Napoule et Riviera Marina pour équipages et invités.",
      },
      {
        title: "Longue distance",
        text: "Trajets toute distance depuis Mandelieu : Cannes, Saint-Tropez, Monaco, Italie et stations de ski.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Château de la Napoule",
        description:
          "Château au bord de l’eau et fondation d’art Henry Clews, face au port.",
      },
      {
        name: "Port de la Napoule & Riviera Marina",
        description:
          "Ports de plaisance au pied de la ville, plages et front de mer.",
      },
      {
        name: "Port La Rague",
        description:
          "Port et chantier naval à la limite de Théoule, côté Estérel.",
      },
      {
        name: "Old Course Cannes-Mandelieu",
        description:
          "L’un des plus anciens golfs de France, au bord de la Siagne.",
      },
      {
        name: "Massif de l’Estérel",
        description:
          "Roches rouges, Rocher de San Peyre et sentiers, aux portes de la ville.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 40 minutes par l’autoroute A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour tout Mandelieu-la-Napoule",
    subheading:
      "Du transfert aéroport aux journées de golf, un service premium entre mer et Estérel.",
    paragraphs: [
      "KS Rent Services assure vos déplacements avec chauffeur privé à Mandelieu-la-Napoule et sur toute la Côte d’Azur : transferts depuis l’aéroport de Nice (environ 40 minutes), journées de golf, accès aux ports de plaisance et trajets longue distance.",
      "Nos chauffeurs bilingues connaissent Mandelieu — le Château de la Napoule, les ports La Rague et La Napoule, l’Old Course et la Route du Mimosa — pour vous déposer au plus près, même en pleine saison ou pendant la Fête du Mimosa.",
      "Entre golf le matin, déjeuner au port et randonnée dans l’Estérel l’après-midi, votre chauffeur enchaîne les étapes sans contrainte de stationnement ni de retour.",
    ],
    bullets: [
      "Suivi de vol & accueil personnalisé",
      "Espace pour sacs de golf et bagages",
      "Liaisons ports & Estérel",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Mandelieu-la-Napoule",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Nous confirmons le véhicule adapté et le forfait.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Ajustements en direct selon le trafic et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Mandelieu-la-Napoule"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Mandelieu ?",
      a: "Comptez environ 40 minutes par l’autoroute A8 dans des conditions normales. Nous assurons les transferts 24h/24 avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et pendant la Fête du Mimosa ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Mandelieu ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, avec espace pour les bagages et les sacs de golf.",
    },
    {
      q: "Emmenez-vous les golfeurs sur les parcours de Mandelieu ?",
      a: "Oui, nous desservons l’Old Course de Cannes-Mandelieu et le golf Riviera, avec un véhicule adapté aux sacs de golf et une attente sur place si besoin.",
    },
    {
      q: "Assurez-vous les liaisons avec les ports de plaisance ?",
      a: "Oui, nous rejoignons le port La Rague, le port de la Napoule et Riviera Marina pour les équipages, propriétaires et invités.",
    },
  ],
  relatedLinks: [
    { label: "Réserver un chauffeur à Cannes", href: "/chauffeur/cannes" },
    { label: "Navette aéroport de Nice", href: "/chauffeur/aeroport" },
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Voitures à louer à Cannes",
      href: "/location/location-voiture-cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Mandelieu-la-Napoule",
    text: "Réservez votre transfert ou votre mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Mandelieu-la-Napoule — VTC 24h/24",
    description:
      "Chauffeur privé et VTC à Mandelieu-la-Napoule : transfert aéroport de Nice (~40 min), golf, ports La Rague et La Napoule. 5,0/5 sur 38 avis. Devis immédiat.",
    canonical: "/chauffeur/mandelieu-la-napoule",
  },
};

export default mandelieuLaNapoule;
